import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

// Feature edit dialog component
const FeaturesDialog = ({ open, onClose, features, onSave, title }) => {
  const [featuresList, setFeaturesList] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setFeaturesList(features || []);
  }, [features]);

  const handleAddFeature = () => {
    if (!newFeature.trim()) {
      setError('Feature description cannot be empty');
      return;
    }
    
    setFeaturesList([...featuresList, newFeature.trim()]);
    setNewFeature('');
    setError('');
  };

  const handleRemoveFeature = (index) => {
    setFeaturesList(featuresList.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(featuresList);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title || 'Edit Features'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs>
              <TextField
                fullWidth
                label="New Feature"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Enter feature description"
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddFeature}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        <List>
          {featuresList.map((feature, index) => (
            <ListItem key={index} divider={index < featuresList.length - 1}>
              <ListItemText primary={feature} />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleRemoveFeature(index)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {featuresList.length === 0 && (
            <ListItem>
              <ListItemText primary="No features added" />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
        >
          Save Features
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Pricing tier card component
const PricingTierCard = ({ tier, onEdit }) => {
  const formatCurrency = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'EUR'
    });
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="div" sx={{ textTransform: 'capitalize' }}>
            {tier.displayName}
          </Typography>
          <Chip 
            label={tier.name} 
            color={
              tier.name === 'free' ? 'default' : 
              tier.name === 'professional' ? 'primary' : 
              'secondary'
            } 
            size="small"
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {tier.description}
        </Typography>
        
        <Typography variant="h4" component="div" sx={{ mb: 2 }}>
          {tier.basePrice === 0 ? 'Free' : formatCurrency(tier.basePrice)}
          {tier.basePrice > 0 && (
            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
              /month
            </Typography>
          )}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Limits
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Monthly API Calls:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {tier.monthlyLimit.toLocaleString()}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                API Keys:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {tier.apiKeyLimit}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Typography variant="subtitle2" gutterBottom>
          Overage Pricing
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Retrieval:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(tier.retrievalPrice)} / call
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Submission:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(tier.submissionPrice)} / call
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Amendment:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(tier.amendmentPrice)} / call
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Retraction:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(tier.retractionPrice)} / call
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Typography variant="subtitle2" gutterBottom>
          Additional Costs
        </Typography>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Additional API Key:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(tier.additionalApiKeyPrice)} / key
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Features ({tier.features.length})
        </Typography>
        <List dense>
          {tier.features.slice(0, 3).map((feature, index) => (
            <ListItem key={index} disableGutters>
              <ListItemText 
                primary={feature} 
                primaryTypographyProps={{ variant: 'body2' }} 
              />
            </ListItem>
          ))}
          {tier.features.length > 3 && (
            <ListItem disableGutters>
              <ListItemText 
                primary={`+ ${tier.features.length - 3} more features`}
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
          )}
        </List>
      </CardContent>
      <CardActions>
        <Button 
          fullWidth 
          variant="contained" 
          color="primary" 
          startIcon={<EditIcon />}
          onClick={() => onEdit(tier)}
        >
          Edit Tier
        </Button>
      </CardActions>
    </Card>
  );
};

// Edit tier dialog component
const EditTierDialog = ({ open, onClose, tier, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    monthlyLimit: 0,
    apiKeyLimit: 0,
    basePrice: 0,
    retrievalPrice: 0,
    submissionPrice: 0,
    amendmentPrice: 0,
    retractionPrice: 0,
    additionalApiKeyPrice: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    if (tier) {
      setFormData({
        name: tier.name || '',
        displayName: tier.displayName || '',
        description: tier.description || '',
        monthlyLimit: tier.monthlyLimit || 0,
        apiKeyLimit: tier.apiKeyLimit || 0,
        basePrice: tier.basePrice || 0,
        retrievalPrice: tier.retrievalPrice || 0,
        submissionPrice: tier.submissionPrice || 0,
        amendmentPrice: tier.amendmentPrice || 0,
        retractionPrice: tier.retractionPrice || 0,
        additionalApiKeyPrice: tier.additionalApiKeyPrice || 0
      });
      setFeatures(tier.features || []);
    }
  }, [tier]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle numeric fields
    if (['monthlyLimit', 'apiKeyLimit', 'basePrice', 'retrievalPrice', 
         'submissionPrice', 'amendmentPrice', 'retractionPrice', 
         'additionalApiKeyPrice'].includes(name)) {
      setFormData({
        ...formData,
        [name]: value === '' ? '' : Number(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleFeaturesUpdate = (updatedFeatures) => {
    setFeatures(updatedFeatures);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate name
      if (formData.name !== tier.name) {
        setError("Tier name cannot be changed");
        setLoading(false);
        return;
      }
      
      // Validate required fields
      if (!formData.displayName || !formData.description) {
        setError("Display name and description are required");
        setLoading(false);
        return;
      }
      
      // Prepare data for submission
      const tierData = {
        ...formData,
        features: features
      };
      
      await onSave(tierData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update pricing tier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Pricing Tier: {tier?.displayName}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tier ID"
                name="name"
                value={formData.name}
                disabled
                helperText="Tier ID cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Limits
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly API Call Limit"
                name="monthlyLimit"
                value={formData.monthlyLimit}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Key Limit"
                name="apiKeyLimit"
                value={formData.apiKeyLimit}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Pricing
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Base Price (in cents)"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Monthly subscription price in cents (e.g. 4900 = €49.00)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Additional API Key Price (in cents)"
                name="additionalApiKeyPrice"
                value={formData.additionalApiKeyPrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Price per additional API key in cents"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Overage Pricing (in cents)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retrieval Price"
                name="retrievalPrice"
                value={formData.retrievalPrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Price per retrieval call above the monthly limit"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Submission Price"
                name="submissionPrice"
                value={formData.submissionPrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Price per submission call above the monthly limit"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amendment Price"
                name="amendmentPrice"
                value={formData.amendmentPrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Price per amendment call above the monthly limit"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retraction Price"
                name="retractionPrice"
                value={formData.retractionPrice}
                onChange={handleInputChange}
                type="number"
                required
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">€</InputAdornment>
                }}
                helperText="Price per retraction call above the monthly limit"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1">
                  Features ({features.length})
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setFeaturesDialogOpen(true)}
                >
                  Edit Features
                </Button>
              </Box>
              <List dense sx={{ bgcolor: 'background.paper', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {features.length > 0 ? (
                  features.map((feature, index) => (
                    <ListItem key={index} divider={index < features.length - 1}>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No features added" />
                  </ListItem>
                )}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      <FeaturesDialog
        open={featuresDialogOpen}
        onClose={() => setFeaturesDialogOpen(false)}
        features={features}
        onSave={handleFeaturesUpdate}
        title={`Edit Features for ${tier?.displayName}`}
      />
    </>
  );
};

function PricingConfiguration() {
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchPricingTiers();
  }, [refreshKey]);

  const fetchPricingTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/admin/pricing-tiers');
      
      if (response.data.status === 'success') {
        setPricingTiers(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch pricing tiers');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setDialogOpen(true);
  };

  const handleSaveTier = async (tierData) => {
      const response = await axios.put(`/api/admin/pricing-tiers/${tierData.name}`, tierData);
      
      if (response.data.status === 'success') {
        // Refresh the list
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error(response.data.message || 'Failed to update pricing tier');
      }
     
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Pricing Configuration
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {pricingTiers.map((tier) => (
            <Grid item xs={12} md={4} key={tier.name}>
              <PricingTierCard tier={tier} onEdit={handleEditTier} />
            </Grid>
          ))}
        </Grid>
      )}
      
      {editingTier && (
        <EditTierDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          tier={editingTier}
          onSave={handleSaveTier}
        />
      )}
    </Box>
  );
}

export default PricingConfiguration; 